import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/useAuth';
import { useI18n } from '../i18n/useI18n';
import { NotificationBell } from '../components/NotificationBell';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import type { UserNotification } from '../services/notifications';

export default function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { t } = useI18n();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Obtener usuario actual
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
      }
    };
    getUser();
  }, []);

  // Realtime para nuevas notificaciones
  useEffect(() => {
    if (!currentUserId || !supabase) return;

    const channel = supabase
      .channel(`notifications-${currentUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_notifications',
          filter: `user_id=eq.${currentUserId}`,
        },
        (payload) => {
          // La campanita se actualizará automáticamente
          console.log('Nueva notificación recibida:', payload.new);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUserId]);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const isPhotographer = profile?.role === 'photographer' || profile?.role === 'superadmin';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Suspension Banner */}
      {profile?.status === 'suspended' && (
        <div className="bg-red-600 text-white px-4 py-3 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center">
              <i className="ri-error-warning-line text-2xl mr-3"></i>
              <div>
                <p className="font-semibold">Account Suspended</p>
                <p className="text-sm text-red-100">
                  Your EasyDigitalDVD services are suspended. Please settle your outstanding balance.
                </p>
              </div>
            </div>
            <a
              href="/app/payments"
              className="px-4 py-2 bg-white text-red-600 rounded-lg font-medium hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              View Invoices
            </a>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gray-50 flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-6 border-b border-gray-200">
            <Link to="/app" className="flex items-center gap-2">
              <img src="https://static.readdy.ai/image/e50d925263add9c47b947a5709171c8c/894de08e385da480c4cd82df0969ba30.png" alt="EasyDigitalDVD" className="h-10" />
            </Link>
          </div>
          <nav className="flex-1 p-4">
            <Link
              to="/app"
              className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors cursor-pointer whitespace-nowrap ${
                location.pathname === '/app'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <i className="ri-dashboard-line text-lg w-5 h-5 flex items-center justify-center"></i>
              <span className="font-medium">{t('navigation.dashboard')}</span>
            </Link>
            {isPhotographer ? (
              <Link
                to="/app/projects"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors cursor-pointer whitespace-nowrap ${
                  isActive('/app/projects')
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <i className="ri-folder-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="font-medium">{t('navigation.projects')}</span>
              </Link>
            ) : (
              <Link
                to="/app/client"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors cursor-pointer whitespace-nowrap ${
                  isActive('/app/client')
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <i className="ri-folder-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="font-medium">{t('client.myProjects')}</span>
              </Link>
            )}
            <Link
              to="/app/payments"
              className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/app/payments')
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <i className="ri-money-dollar-circle-line text-lg w-5 h-5 flex items-center justify-center"></i>
              <span className="font-medium">{t('billing.navTitle')}</span>
            </Link>
            {isPhotographer && (
              <Link
                to="/app/settings"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors cursor-pointer whitespace-nowrap ${
                  isActive('/app/settings')
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <i className="ri-settings-3-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="font-medium">{t('settings.navTitle')}</span>
              </Link>
            )}
          </nav>
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-line text-lg w-5 h-5 flex items-center justify-center"></i>
              <span className="font-medium">{t('auth.logout')}</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          <header className="bg-white border-b border-gray-200 px-8 py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-semibold text-gray-900">{t('navigation.dashboard')}</h1>
              <div className="flex items-center gap-4">
                <NotificationBell />
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{profile?.full_name || 'Usuario'}</p>
                    <p className="text-xs text-gray-500">{profile?.email}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center cursor-pointer">
                    <span className="text-white font-medium text-sm">
                      {profile?.full_name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </header>
          <main className="flex-1 p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
